import * as THREE from 'three'
import { markRaw } from 'vue'
import D20MaterialService from './d20MaterialService.js'
import DiceRitualEnvironment from './DiceRitualEnvironment.js'
import { createFaceTexture } from './diceFaceTexture.js'
import { getDiceTheme } from './diceThemes.js'

const DICE_SCALE = 0.5

export class D20Service {
    constructor(themeId = null) {
        this.theme = getDiceTheme(themeId)
        this.scene = null
        this.camera = null
        this.renderer = null
        this.dice = null
        this.diceArray = [] // Array to hold multiple dice
        this.diceCount = 1
        this.userTextureImg = null
        this.surfaceImg = null
        this.isInitialized = false
        this.materialService = markRaw(new D20MaterialService())
        this.materialService.setMaterialType(this.theme.material)
        this.environment = markRaw(new DiceRitualEnvironment(this.theme))
        this.textureCache = new Map()

        // Face orientation targets, recomputed whenever the camera moves.
        this.faceDirection = markRaw(new THREE.Vector3(0, 1, 0))
        this.screenUp = markRaw(new THREE.Vector3(0, 0, -1))

        // Regular icosahedron: the vertex constant is the golden ratio. Anything
        // else yields an irregular solid whose faces are not equiprobable.
        this.t = (1 + Math.sqrt(5)) / 2
        this.vertices = [
            [-1, this.t, 0], [1, this.t, 0], [-1, -this.t, 0], [1, -this.t, 0],
            [0, -1, this.t], [0, 1, this.t], [0, -1, -this.t], [0, 1, -this.t],
            [this.t, 0, -1], [this.t, 0, 1], [-this.t, 0, -1], [-this.t, 0, 1]
        ]

        this.faces = [
            [0, 11, 5], [0, 5, 1], [0, 1, 7], [0, 7, 10], [0, 10, 11],
            [1, 5, 9], [5, 11, 4], [11, 10, 2], [10, 7, 6], [7, 1, 8],
            [3, 9, 4], [3, 4, 2], [3, 2, 6], [3, 6, 8], [3, 8, 9],
            [4, 9, 5], [2, 4, 11], [6, 2, 10], [8, 6, 7], [9, 8, 1]
        ]

        this.faceNumbers = Array.from({ length: 20 }, (_, i) => i + 1)
        this.faceNormals = this.calculateFaceNormals()
    }

    calculateFaceNormals() {
        return this.faces.map((f) => {
            const [a, b, c] = f.map((i) => new THREE.Vector3(...this.vertices[i]))
            return new THREE.Vector3()
                .crossVectors(b.clone().sub(a), c.clone().sub(a))
                .normalize()
        })
    }

    async initializeScene(canvas, width, height) {
        try {
            // Transparent: the chamber comes from the photographic plate behind
            // the canvas, and only the die and its shadow are drawn here.
            this.scene = markRaw(new THREE.Scene())

            // Matches the plate's eye level — looking down into the ring at the
            // same pitch the photograph was shot at.
            this.camera = markRaw(new THREE.PerspectiveCamera(40, width / height, 0.1, 120))
            this.camera.position.set(0, 8.2, 9.7)
            this.camera.lookAt(0, 0.5, 0)
            this.updateFaceOrientation()

            this.renderer = markRaw(new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true }))
            this.renderer.setSize(width, height)
            this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
            this.renderer.shadowMap.enabled = true
            this.renderer.shadowMap.type = THREE.PCFSoftShadowMap

            this.renderer.outputColorSpace = THREE.SRGBColorSpace
            this.renderer.toneMapping = THREE.ACESFilmicToneMapping
            this.renderer.toneMappingExposure = 1.15

            await this.loadThemeSurface()
            this.materialService.initializeShaders()
            this.setupLighting()
            this.environment.build(this.scene)

            this.isInitialized = true
            return true
        } catch (error) {
            console.error('Failed to initialize D20 scene:', error)
            return false
        }
    }

    /**
     * A theme may hand the die a rendered material plate instead of a painted
     * one. It is loaded before any face is rasterised; if it cannot be loaded
     * the faces fall back to the painted stone rather than failing the scene.
     */
    async loadThemeSurface() {
        const src = this.theme.face.surface
        if (!src) return null

        this.surfaceImg = await new Promise((resolve) => {
            const img = new Image()
            img.onload = () => resolve(img)
            img.onerror = () => {
                console.warn(`Dice theme "${this.theme.id}" surface failed to load`)
                resolve(null)
            }
            img.src = src
        })
        return this.surfaceImg
    }

    setupLighting() {
        // Lit to match the plate: almost no ambient, a key from high above that
        // drops a real shadow onto the stone, and bounce from the ring. Which
        // colours those are is the theme's call.
        const light = this.theme.lighting
        this.scene.add(new THREE.AmbientLight(light.ambient, light.ambientIntensity))
        this.scene.add(new THREE.HemisphereLight(light.hemiSky, light.hemiGround, light.hemiIntensity))

        const keyLight = new THREE.SpotLight(light.key, light.keyIntensity, 20, Math.PI / 5.5, 0.6, 2)
        keyLight.position.set(-1.6, 7.4, 2.6)
        keyLight.target.position.set(0, 0.7, 0)
        keyLight.castShadow = true
        keyLight.shadow.mapSize.set(1024, 1024)
        keyLight.shadow.bias = -0.0012
        keyLight.shadow.camera.near = 1
        keyLight.shadow.camera.far = 22
        this.scene.add(keyLight)
        this.scene.add(keyLight.target)

        const rimLight = new THREE.DirectionalLight(light.rim, light.rimIntensity)
        rimLight.position.set(3.6, 2.4, -4.5)
        this.scene.add(rimLight)
    }

    /**
     * A landed die rests on a face, so the winning face points straight up. The
     * numeral is then spun about that axis to sit upright from the camera's
     * azimuth — legible without cheating the die off the floor.
     */
    updateFaceOrientation() {
        this.faceDirection.set(0, 1, 0)
        this.screenUp
            .set(-this.camera.position.x, 0, -this.camera.position.z)
            .normalize()

        if (this.screenUp.lengthSq() < 1e-6) {
            this.screenUp.set(0, 0, -1)
        }
    }

    setUserTexture(imageFile) {
        return new Promise((resolve) => {
            if (!imageFile) {
                this.userTextureImg = null
                this.clearTextureCache()
                resolve(true)
                return
            }

            const reader = new FileReader()
            reader.onload = (event) => {
                const img = new Image()
                img.onload = () => {
                    this.userTextureImg = img
                    this.clearTextureCache()
                    resolve(true)
                }
                img.onerror = (error) => {
                    console.error('Failed to load custom texture:', error)
                    resolve(false)
                }
                // Set crossOrigin before src for CORS issues
                img.crossOrigin = 'anonymous'
                img.src = event.target.result
            }
            reader.onerror = (error) => {
                console.error('Failed to read file:', error)
                resolve(false)
            }
            reader.readAsDataURL(imageFile)
        })
    }

    /**
     * Face textures are expensive to rasterise, so they are built once per
     * (number, highlight) pair and shared by every die in the scene.
     */
    getFaceTextures(num, highlight = false) {
        const base = this.userTextureImg ? 'user' : (this.surfaceImg ? 'surface' : 'stone')
        const key = `${num}:${highlight ? 'hi' : 'base'}:${base}`
        if (!this.textureCache.has(key)) {
            this.textureCache.set(key, createFaceTexture(num, {
                highlight,
                userImage: this.userTextureImg,
                surfaceImage: this.surfaceImg,
                theme: this.theme
            }))
        }
        return this.textureCache.get(key)
    }

    clearTextureCache() {
        this.textureCache.forEach(({ map, emissiveMap }) => {
            map?.dispose()
            emissiveMap?.dispose()
        })
        this.textureCache.clear()
    }

    buildD20Mesh(highlightFace = -1) {
        const geometry = new THREE.BufferGeometry()
        const positions = []
        const normals = []
        const uvs = []
        const groups = []
        const materials = []

        this.faces.forEach((face, faceIndex) => {
            const [v1, v2, v3] = face.map((idx) => new THREE.Vector3(...this.vertices[idx]))

            // Positions
            positions.push(v1.x, v1.y, v1.z, v2.x, v2.y, v2.z, v3.x, v3.y, v3.z)

            // Normals
            const normal = new THREE.Vector3()
                .crossVectors(v2.clone().sub(v1), v3.clone().sub(v1))
                .normalize()
            normals.push(normal.x, normal.y, normal.z, normal.x, normal.y, normal.z, normal.x, normal.y, normal.z)

            // UVs - simple uniform mapping that preserves text readability
            uvs.push(0.1, 0.2, 0.9, 0.2, 0.5, 0.85)

            const textures = this.getFaceTextures(
                this.faceNumbers[faceIndex],
                faceIndex === highlightFace
            )

            const material = this.materialService.createMaterial(
                this.materialService.getCurrentMaterialType(),
                textures,
                this.materialService.getCurrentShaderType()
            )
            material.userData.highlighted = faceIndex === highlightFace

            materials.push(material)
            groups.push({ start: faceIndex * 3, count: 3, materialIndex: faceIndex })
        })

        geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3))
        geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3))
        geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2))
        groups.forEach((g) => geometry.addGroup(g.start, g.count, g.materialIndex))

        const mesh = markRaw(new THREE.Mesh(geometry, materials))
        mesh.scale.setScalar(DICE_SCALE)
        return mesh
    }

    /**
     * Orientation that puts `faceIndex` squarely toward the camera with its
     * numeral upright. Landing is exact: the roll ends on this quaternion, so the
     * face shown is always the one the backend rolled.
     */
    calculateTargetQuaternion(faceIndex) {
        const normal = this.faceNormals[faceIndex].clone()
        const align = new THREE.Quaternion().setFromUnitVectors(normal, this.faceDirection)

        // The UV layout maps the third vertex of each face to the top of the
        // texture, so that vertex marks "up" for the printed numeral.
        const face = this.faces[faceIndex]
        const corners = face.map((i) => new THREE.Vector3(...this.vertices[i]))
        const centroid = corners
            .reduce((acc, v) => acc.add(v), new THREE.Vector3())
            .multiplyScalar(1 / 3)

        const apex = corners[2].clone().sub(centroid).applyQuaternion(align)
        apex.projectOnPlane(this.faceDirection).normalize()

        const angle = Math.atan2(
            apex.clone().cross(this.screenUp).dot(this.faceDirection),
            apex.dot(this.screenUp)
        )
        const upright = new THREE.Quaternion().setFromAxisAngle(this.faceDirection, angle)

        return upright.multiply(align)
    }

    /** Icosahedron data the rigid-body simulation needs. */
    getPhysicsGeometry() {
        return {
            vertices: this.vertices,
            faces: this.faces,
            faceNormals: this.faceNormals,
            faceNumbers: this.faceNumbers,
            scale: DICE_SCALE
        }
    }

    /** Retained for callers that still expect Euler output. */
    calculateTargetRotation(faceIndex) {
        const euler = new THREE.Euler().setFromQuaternion(this.calculateTargetQuaternion(faceIndex))
        return new THREE.Vector3(euler.x, euler.y, euler.z)
    }

    /**
     * Lights the landed face in place. Rebuilding the mesh here would reset the
     * die's orientation, which is what the old implementation had to work around.
     */
    highlightFace(faceNumber, diceIndex = 0) {
        const dice = this.diceArray[diceIndex]
        if (!dice) return false

        // A face number of -1 or null clears the highlight rather than setting one.
        const faceIndex = this.faceNumbers.indexOf(faceNumber)

        dice.material.forEach((material, index) => {
            const shouldHighlight = index === faceIndex
            if (material.userData.highlighted === shouldHighlight) return

            const textures = this.getFaceTextures(this.faceNumbers[index], shouldHighlight)
            material.map = textures.map
            if ('emissiveMap' in material) {
                material.emissiveMap = textures.emissiveMap
                material.emissiveIntensity = shouldHighlight ? 2.2 : 0.85
            }
            material.userData.highlighted = shouldHighlight
            material.needsUpdate = true
        })

        return true
    }

    clearHighlights() {
        this.diceArray.forEach((_, index) => this.highlightFace(null, index))
    }

    createDice(highlightFace = -1) {
        // Clean up existing dice
        this.clearAllDice()

        if (this.diceCount === 1) {
            // Single dice mode - original behavior
            this.dice = this.buildD20Mesh(highlightFace)
            this.dice.castShadow = this.dice.receiveShadow = true
            this.dice.position.set(0, 0.76, 0)
            this.scene.add(this.dice)
            this.diceArray = [this.dice]
        } else {
            // Multiple dice mode
            this.createMultipleDice(this.diceCount, highlightFace)
        }

        return this.diceArray
    }

    createDiceWithIndividualHighlights(highlightFaces = [], preserveRotations = true) {
        // Store current rotations before clearing dice
        const currentRotations = []
        if (preserveRotations && this.diceArray.length > 0) {
            this.diceArray.forEach(dice => {
                if (dice) {
                    currentRotations.push({
                        x: dice.rotation.x,
                        y: dice.rotation.y,
                        z: dice.rotation.z
                    })
                }
            })
        }

        // Clean up existing dice
        this.clearAllDice()

        if (this.diceCount === 1) {
            // Single dice mode - convert face number to face index
            const highlight = Array.isArray(highlightFaces) ? highlightFaces[0] : highlightFaces
            const faceIndex = (highlight !== undefined && highlight !== -1)
                ? this.faceNumbers.indexOf(highlight)
                : -1
            this.dice = this.buildD20Mesh(faceIndex)
            this.dice.castShadow = this.dice.receiveShadow = true
            this.dice.position.set(0, 0.76, 0)

            // Restore rotation if available
            if (currentRotations.length > 0) {
                const rot = currentRotations[0]
                this.dice.rotation.set(rot.x, rot.y, rot.z)
            }

            this.scene.add(this.dice)
            this.diceArray = [this.dice]
        } else {
            // Multiple dice mode with individual highlights
            this.createMultipleDiceWithHighlights(this.diceCount, highlightFaces, currentRotations)
        }

        return this.diceArray
    }

    createMultipleDice(count, highlightFace = -1) {
        this.diceCount = count
        this.clearAllDice()

        const spacing = 3.5 // Distance between dice
        // Center the dice around origin point (0,0,0)
        const totalWidth = (count - 1) * spacing
        const startOffset = -totalWidth / 2

        for (let i = 0; i < count; i++) {
            const diceObject = this.buildD20Mesh(highlightFace)
            diceObject.castShadow = diceObject.receiveShadow = true

            // Position dice centered around origin
            const xOffset = startOffset + (i * spacing)
            diceObject.position.set(xOffset, 0.76, 0)

            this.scene.add(diceObject)
            this.diceArray.push(diceObject)
        }

        // Keep reference to first dice for backward compatibility
        this.dice = this.diceArray[0]

        return this.diceArray
    }

    createMultipleDiceWithHighlights(count, highlightFaces = [], preservedRotations = []) {
        this.diceCount = count
        this.clearAllDice()

        const spacing = 3.5 // Distance between dice
        // Center the dice around origin point (0,0,0)
        const totalWidth = (count - 1) * spacing
        const startOffset = -totalWidth / 2

        for (let i = 0; i < count; i++) {
            // Get highlight face for this specific dice
            const highlightFace = Array.isArray(highlightFaces) ? highlightFaces[i] : highlightFaces
            const faceIndex = (highlightFace !== undefined && highlightFace !== -1)
                ? this.faceNumbers.indexOf(highlightFace)
                : -1

            const diceObject = this.buildD20Mesh(faceIndex)
            diceObject.castShadow = diceObject.receiveShadow = true

            // Position dice centered around origin
            const xOffset = startOffset + (i * spacing)
            diceObject.position.set(xOffset, 0.76, 0)

            // Restore rotation if available
            if (preservedRotations.length > i) {
                const rot = preservedRotations[i]
                diceObject.rotation.set(rot.x, rot.y, rot.z)
            }

            this.scene.add(diceObject)
            this.diceArray.push(diceObject)
        }

        // Keep reference to first dice for backward compatibility
        this.dice = this.diceArray[0]

        return this.diceArray
    }

    clearAllDice() {
        // Clean up existing dice array
        this.diceArray.forEach(diceObj => {
            if (diceObj) {
                this.scene.remove(diceObj)
                diceObj.geometry.dispose()
                diceObj.material.forEach((m) => m.dispose())
            }
        })
        this.diceArray = []

        // Clean up single dice reference
        if (this.dice) {
            this.scene.remove(this.dice)
            this.dice.geometry.dispose()
            this.dice.material.forEach((m) => m.dispose())
            this.dice = null
        }
    }

    setCameraView(viewType) {
        switch (viewType) {
            case 'top':
                this.camera.position.set(0, 7.5, 0.001)
                break
            case 'side':
                this.camera.position.set(0, 2.2, 7)
                break
            case 'angle':
            default:
                this.camera.position.set(0, 8.2, 9.7)
                break
        }
        this.camera.lookAt(0, 0.5, 0)
        this.updateFaceOrientation()
    }

    toggleWireframe() {
        if (this.diceArray.length > 0) {
            const isWireframe = this.diceArray[0].material[0].wireframe
            this.diceArray.forEach(diceObj => {
                if (diceObj && diceObj.material) {
                    diceObj.material.forEach((material) => {
                        material.wireframe = !isWireframe
                    })
                }
            })
            return !isWireframe
        }
        return false
    }

    updateDiceQuaternion(quaternion, diceIndex = 0) {
        if (this.diceArray[diceIndex]) {
            this.diceArray[diceIndex].quaternion.copy(quaternion)
        }
        if (diceIndex === 0 && this.dice) {
            this.dice.quaternion.copy(quaternion)
        }
    }

    updateDiceRotation(rotation, diceIndex = 0) {
        if (this.diceArray[diceIndex]) {
            this.diceArray[diceIndex].rotation.set(rotation.x, rotation.y, rotation.z)
        }
        // Update first dice for backward compatibility
        if (diceIndex === 0 && this.dice) {
            this.dice.rotation.set(rotation.x, rotation.y, rotation.z)
        }
    }

    updateDicePosition(position, diceIndex = 0) {
        if (this.diceArray[diceIndex]) {
            // Each die's roll state carries its own rest position, so the animated
            // position is authoritative for single and multi-dice layouts alike.
            this.diceArray[diceIndex].position.copy(position)
        }
        // Update first dice for backward compatibility
        if (diceIndex === 0 && this.dice) {
            this.dice.position.copy(position)
        }
    }

    getDiceOriginalPosition(diceIndex) {
        if (this.diceCount === 1) {
            return { x: 0, y: 1, z: 0 }
        }

        const spacing = 3.5
        const totalWidth = (this.diceCount - 1) * spacing
        const startOffset = -totalWidth / 2
        const xOffset = startOffset + (diceIndex * spacing)

        return { x: xOffset, y: 1, z: 0 }
    }

    updateAllDiceRotation(rotations) {
        if (Array.isArray(rotations)) {
            rotations.forEach((rotation, index) => {
                this.updateDiceRotation(rotation, index)
            })
        } else {
            // Single rotation for all dice
            this.diceArray.forEach((diceObj, index) => {
                this.updateDiceRotation(rotations, index)
            })
        }
    }

    updateAllDicePosition(positions) {
        if (Array.isArray(positions)) {
            positions.forEach((position, index) => {
                this.updateDicePosition(position, index)
            })
        } else {
            // Single position for all dice (not recommended but supported)
            this.diceArray.forEach((diceObj, index) => {
                this.updateDicePosition(positions, index)
            })
        }
    }

    render(deltaTime = 0) {
        if (this.renderer && this.scene && this.camera) {
            this.materialService.updateShaderUniforms(deltaTime)
            this.environment.update(deltaTime)
            this.renderer.render(this.scene, this.camera)
        }
    }

    resize(width, height) {
        if (this.camera && this.renderer) {
            this.camera.aspect = width / height
            this.camera.updateProjectionMatrix()
            this.renderer.setSize(width, height)
        }
    }

    findTopFace(diceIndex = 0) {
        const targetDice = this.diceArray[diceIndex] || this.dice
        if (!targetDice) return 1

        let bestIndex = 0
        let maxY = -Infinity

        this.faces.forEach((face, index) => {
            const normal = this.faceNormals[index].clone()
            normal.applyMatrix3(new THREE.Matrix3().getNormalMatrix(targetDice.matrixWorld))
            if (normal.y > maxY) {
                maxY = normal.y
                bestIndex = index
            }
        })

        return this.faceNumbers[bestIndex]
    }

    findAllTopFaces() {
        return this.diceArray.map((diceObj, index) => this.findTopFace(index))
    }

    // Material and shader control methods
    setMaterialType(materialType) {
        if (this.materialService.setMaterialType(materialType)) {
            // Recreate dice with new material
            if (this.dice) {
                this.createDice()
            }
            return true
        }
        return false
    }

    setShaderType(shaderType) {
        if (this.materialService.setShaderType(shaderType)) {
            // Recreate dice with new shader
            if (this.dice) {
                this.createDice()
            }
            return true
        }
        return false
    }

    getMaterialTypes() {
        return this.materialService.getMaterialTypes()
    }

    getShaderTypes() {
        return this.materialService.getShaderTypes()
    }

    getCurrentMaterialType() {
        return this.materialService.getCurrentMaterialType()
    }

    getCurrentShaderType() {
        return this.materialService.getCurrentShaderType()
    }

    dispose() {
        // Clean up all dice using the existing method
        this.clearAllDice()
        this.environment.dispose()
        this.clearTextureCache()

        if (this.renderer) {
            this.renderer.dispose()
        }

        this.scene = null
        this.camera = null
        this.renderer = null
        this.dice = null
        this.diceArray = []
        this.isInitialized = false
    }
}

export default D20Service